import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import axiosInstance from '../../../utils/axios';

const AuditFilters = ({ onFiltersChange }) => {
  const [filters, setFilters] = useState({
    dateRange: { start: '', end: '' },
    department: '',
    category_id: '',
    status: '',
    priority: '',
    assignedTo: '',
    location: '',
    search: '',
  });

  const [isExpanded, setIsExpanded] = useState(false);
  const [filterOptions, setFilterOptions] = useState({
    departments: [],
    categories: [],
    locations: [],
    auditors: [],
  });
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Load real dynamic filter options from API
  useEffect(() => {
    let isMounted = true;
    const fetchOptions = async () => {
      try {
        setLoadingOptions(true);
        const res = await axiosInstance.get('/audit/filters-data');
        if (isMounted && res.data?.success) {
          const data = res.data.data;
          setFilterOptions({
            departments: (data.departments || []).map((d) => ({ value: d, label: d })),
            categories: (data.categories || []).map((c) => ({ value: c.id.toString(), label: c.name })),
            locations: (data.locations || []).map((l) => ({ value: l, label: l })),
            auditors: (data.auditors || []).map((a) => ({
              value: a.name,
              label: `${a.name} (${a.department || 'Auditor'})`,
            })),
          });
        }
      } catch (err) {
        console.error('Failed to load dynamic audit filter options:', err);
      } finally {
        if (isMounted) setLoadingOptions(false);
      }
    };

    fetchOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending Verification' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'verified', label: 'Verified' },
    { value: 'exception', label: 'Exception' },
    { value: 'escalated', label: 'Escalated' },
  ];

  const priorityOptions = [
    { value: '', label: 'All Priorities' },
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
  ];

  const departmentSelectOptions = [
    { value: '', label: 'All Departments' },
    ...filterOptions.departments,
  ];

  const categorySelectOptions = [
    { value: '', label: 'All Asset Types' },
    ...filterOptions.categories,
  ];

  const locationSelectOptions = [
    { value: '', label: 'All Locations' },
    ...filterOptions.locations,
  ];

  const assignedToSelectOptions = [
    { value: '', label: 'All Auditors & Custodians' },
    ...filterOptions.auditors,
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleDateRangeChange = (type, value) => {
    const newDateRange = { ...filters.dateRange, [type]: value };
    const newFilters = { ...filters, dateRange: newDateRange };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    const cleared = {
      dateRange: { start: '', end: '' },
      department: '',
      category_id: '',
      status: '',
      priority: '',
      assignedTo: '',
      location: '',
      search: '',
    };
    setFilters(cleared);
    onFiltersChange(cleared);
  };

  const getActiveFilterCount = () => {
    let count = 0;
    if (filters.dateRange?.start || filters.dateRange?.end) count++;
    if (filters.department) count++;
    if (filters.category_id) count++;
    if (filters.status) count++;
    if (filters.priority) count++;
    if (filters.assignedTo) count++;
    if (filters.location) count++;
    if (filters.search) count++;
    return count;
  };

  const activeFilterCount = getActiveFilterCount();

  return (
    <div className="bg-card border border-border rounded-lg shadow-subtle">
      {/* Header Bar */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
              <Icon name="Filter" size={16} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-card-foreground">Audit Scope & Filter Engine</h3>
              <p className="text-xs text-muted-foreground">Dynamic filters connected to live asset inventory</p>
            </div>
            {activeFilterCount > 0 && (
              <span className="bg-primary text-primary-foreground text-xs px-2.5 py-0.5 rounded-full font-semibold">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-muted-foreground hover:text-foreground h-8 text-xs"
              >
                <Icon name="X" size={14} className="mr-1.5" />
                Clear All
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="h-8 text-xs font-medium"
            >
              <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} className="mr-1.5" />
              {isExpanded ? 'Fewer Filters' : 'More Filters'}
            </Button>
          </div>
        </div>
      </div>

      {/* Primary Quick Filters */}
      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Quick Search</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Asset ID, brand, model..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="w-full px-3 py-1.5 pl-8 text-xs rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              />
              <Icon name="Search" size={14} className="absolute left-2.5 top-2 text-muted-foreground" />
            </div>
          </div>

          <Select
            label="Verification Status"
            options={statusOptions}
            value={filters.status}
            onChange={(val) => handleFilterChange('status', val)}
            placeholder="All Statuses"
          />

          <Select
            label="Priority Level"
            options={priorityOptions}
            value={filters.priority}
            onChange={(val) => handleFilterChange('priority', val)}
            placeholder="All Priorities"
          />

          <Select
            label="Department"
            options={departmentSelectOptions}
            value={filters.department}
            onChange={(val) => handleFilterChange('department', val)}
            placeholder="All Departments"
            loading={loadingOptions}
          />

          <Select
            label="Asset Type"
            options={categorySelectOptions}
            value={filters.category_id}
            onChange={(val) => handleFilterChange('category_id', val)}
            placeholder="All Types"
            loading={loadingOptions}
          />
        </div>
      </div>

      {/* Expanded Advanced Filters */}
      {isExpanded && (
        <div className="p-4 space-y-4 bg-muted/10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <Select
              label="Auditor / Assigned Custodian"
              options={assignedToSelectOptions}
              value={filters.assignedTo}
              onChange={(val) => handleFilterChange('assignedTo', val)}
              placeholder="All Auditors"
              loading={loadingOptions}
            />

            <Select
              label="Physical Location"
              options={locationSelectOptions}
              value={filters.location}
              onChange={(val) => handleFilterChange('location', val)}
              placeholder="All Locations"
              loading={loadingOptions}
            />

            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">Audit Verification Range</label>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={filters.dateRange.start}
                  onChange={(e) => handleDateRangeChange('start', e.target.value)}
                  className="px-2 py-1 text-xs rounded-md border border-input bg-background text-foreground"
                />
                <input
                  type="date"
                  value={filters.dateRange.end}
                  onChange={(e) => handleDateRangeChange('end', e.target.value)}
                  className="px-2 py-1 text-xs rounded-md border border-input bg-background text-foreground"
                />
              </div>
            </div>
          </div>

          {/* Quick Filter Presets */}
          <div className="pt-2 border-t border-border flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center space-x-1.5 flex-wrap gap-1.5">
              <span className="text-xs font-medium text-muted-foreground mr-1">Quick Scopes:</span>
              <button
                type="button"
                onClick={() => {
                  const p = { ...filters, status: 'pending', priority: 'critical' };
                  setFilters(p);
                  onFiltersChange(p);
                }}
                className="text-xs px-2.5 py-1 rounded bg-rose-500/10 text-rose-600 hover:bg-rose-500/20 font-medium transition"
              >
                Critical Pending
              </button>
              <button
                type="button"
                onClick={() => {
                  const p = { ...filters, status: 'exception' };
                  setFilters(p);
                  onFiltersChange(p);
                }}
                className="text-xs px-2.5 py-1 rounded bg-amber-500/10 text-amber-600 hover:bg-amber-500/20 font-medium transition"
              >
                All Exceptions
              </button>
              <button
                type="button"
                onClick={() => {
                  const p = { ...filters, status: 'verified' };
                  setFilters(p);
                  onFiltersChange(p);
                }}
                className="text-xs px-2.5 py-1 rounded bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 font-medium transition"
              >
                Verified Assets
              </button>
            </div>
            <div className="text-xs text-muted-foreground">
              {activeFilterCount > 0 ? `${activeFilterCount} active filter(s)` : 'Showing full quarterly inventory'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditFilters;
